import axios from 'axios';
import type { AxiosInstance, AxiosError, AxiosRequestConfig, InternalAxiosRequestConfig } from 'axios';
import type {
  Response,
  SibsAccountsResponse,
  SibsBalancesResponse,
  SibsTransactionsResponse,
  StoredConsentDto,
  ConsentStatusResponse,
  ConsentFinalizeResponse,
  BankAccountSyncStatus,
  BillingStatus,
  PlanDto,
  CreditPackDto,
  PaymentDto,
  CheckoutInitiateData,
  CheckoutStatusData,
  ProductUpdatesResponse,
  ProductUpdatePreferences,
} from '@/types';

class ApiError extends Error {
  constructor(
    message: string,
    public errorCode?: string
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

// Authentication error handler — set by the auth store
let authErrorHandler: (() => void) | null = null;

export const setAuthErrorHandler = (handler: () => void) => {
  authErrorHandler = handler;
};

// Single-flight session keep-alive / refresh probe. Hits the BFF's auth-status endpoint,
// which runs the server-side refresh gate; a burst of 401s shares one in-flight request.
let refreshInFlight: Promise<boolean> | null = null;
export const tryRefreshSession = (): Promise<boolean> => {
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = (async () => {
    try {
      const r = await fetch('/api/auth/user', { credentials: 'include' });
      if (!r.ok) return false;
      const d = await r.json();
      return d?.isAuthenticated === true;
    } catch {
      return false;
    } finally {
      setTimeout(() => { refreshInFlight = null; }, 0);
    }
  })();
  return refreshInFlight;
};

// Create axios instance. BFF model: auth is the session cookie (no bearer token in the
// browser); CSRF token is read from the XSRF-TOKEN cookie and echoed as X-XSRF-TOKEN.
const createApiClient = (): AxiosInstance => {
  const client = axios.create({
    baseURL: '/api',
    timeout: 30000,
    withCredentials: true,
    xsrfCookieName: 'XSRF-TOKEN',
    xsrfHeaderName: 'X-XSRF-TOKEN',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    },
  });

  // No X-Client-Id override: the active client is the auth-server session's currently-logged
  // client, changed via POST /clients/switch (which drives a full OIDC re-login). The cookie
  // carries auth; there is no bearer token or client-side tenant override to attach here.

  // Response interceptor for error handling
  client.interceptors.response.use(
    (response) => {
      // Check for application-level errors in successful HTTP responses
      if (response.data?.isError === true) {
        const error = response.data.error;

        if (error === 'General.WithoutPermission') {
          window.location.href = '/no-permission';
        }

        throw new ApiError(
          response.data.directError || 'Operation failed',
          error
        );
      }

      return response;
    },
    (error: AxiosError) => {
      if (axios.isAxiosError(error)) {
        // A role denial (authenticated, but lacking the MagniBackofficeUser role) is NOT a
        // login problem — send the user to the missing-permissions page. Bouncing to /login
        // would just loop (login succeeds, the next call 403s again).
        const data403 = error.response?.data as { code?: string } | undefined;
        if (error.response?.status === 403 && data403?.code === 'missing_required_role') {
          if (window.location.pathname !== '/no-permission') {
            window.location.href = '/no-permission';
          }
          return Promise.reject(new ApiError('Forbidden', 'auth.forbidden'));
        }

        if (error.response?.status === 401) {
          // Try one server-side refresh before giving up. A near-expiry access token is
          // refreshed by the BFF's enrichment middleware; a transient 401 then succeeds on retry.
          const cfg = error.config as (InternalAxiosRequestConfig & { _retried?: boolean }) | undefined;
          if (cfg && !cfg._retried) {
            cfg._retried = true;
            return tryRefreshSession().then((ok) => {
              if (ok) return apiClient.request(cfg);
              if (authErrorHandler) authErrorHandler();
              return Promise.reject(new ApiError('Unauthorized', 'auth.unauthorized'));
            });
          }
          if (authErrorHandler) authErrorHandler();
          return Promise.reject(new ApiError('Unauthorized', 'auth.unauthorized'));
        }

        if (error.response?.status === 403) {
          // Forbidden that isn't the role gate (handled above) — not a login problem.
          if (authErrorHandler) authErrorHandler();
          return Promise.reject(new ApiError('Forbidden', 'auth.forbidden'));
        }

        const data = error.response?.data as Response<unknown>;
        throw new ApiError(
          data?.directError || error.message,
          data?.error
        );
      }

      throw error;
    }
  );

  return client;
};

const apiClient = createApiClient();

// Generic HTTP methods

const get = async <T>(url: string, params?: Record<string, unknown>): Promise<Response<T>> => {
  const response = await apiClient.get<Response<T>>(url, { params });
  return response.data;
};

const post = async <T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<Response<T>> => {
  const response = await apiClient.post<Response<T>>(url, data, config);
  return response.data;
};

const put = async <T>(url: string, data?: unknown): Promise<Response<T>> => {
  const response = await apiClient.put<Response<T>>(url, data);
  return response.data;
};

const del = async <T>(url: string): Promise<Response<T>> => {
  const response = await apiClient.delete<Response<T>>(url);
  return response.data;
};

// SIBS Account API methods

const getSibsAccounts = async (
  aspspCde: string,
  consentId: string
): Promise<Response<SibsAccountsResponse>> => {
  return get<SibsAccountsResponse>(`/sibs/accounts/${aspspCde}/${consentId}`);
};

const getSibsBalances = async (
  aspspCde: string,
  consentId: string,
  accountId: string
): Promise<Response<SibsBalancesResponse>> => {
  return get<SibsBalancesResponse>(`/sibs/accounts/${aspspCde}/${consentId}/${accountId}/balances`);
};

const getSibsTransactions = async (
  aspspCde: string,
  consentId: string,
  accountId: string,
  dateFrom?: string,
  dateTo?: string
): Promise<Response<SibsTransactionsResponse>> => {
  const params: Record<string, string> = {};
  if (dateFrom) params.dateFrom = dateFrom;
  if (dateTo) params.dateTo = dateTo;

  return get<SibsTransactionsResponse>(
    `/sibs/accounts/${aspspCde}/${consentId}/${accountId}/transactions`,
    params
  );
};

// Consent API methods

const getMyConsents = async (includeExpired = false): Promise<Response<StoredConsentDto[]>> => {
  return get<StoredConsentDto[]>('/consent/my', { includeExpired });
};

const getConsentStatus = async (
  consentId: string,
  aspspCde: string
): Promise<Response<ConsentStatusResponse>> => {
  return get<ConsentStatusResponse>(`/consent/${consentId}`, { aspspCde });
};

/**
 * Finalize (acquire) consent on an account by its stored ConsentId — decoupled from the
 * SIBS bank-redirect callback. On success the account is "connected" and survives a later
 * transaction-sync failure.
 */
const finalizeConsent = async (
  accountId: number
): Promise<Response<ConsentFinalizeResponse>> => {
  return post<ConsentFinalizeResponse>(`/bankaccount/${accountId}/finalize-consent`);
};

const SYNC_POLL_MS = 2000;
// A first sync of a busy account reads up to 90 days in windows; give it room before giving up on the wait.
const SYNC_WAIT_MS = 45 * 60 * 1000;

/**
 * Sync an account: starts it on the server (or joins the one already running — a consent's first
 * sync starts there by itself) and follows it until it ends, reporting progress. Syncs run in the
 * background, so nothing here is held open while the bank is read. Resolves with the final status
 * when it completed; throws with the reason otherwise.
 */
const syncAccount = async (
  accountId: number,
  onProgress?: (status: BankAccountSyncStatus) => void
): Promise<BankAccountSyncStatus> => {
  let status = (await post<BankAccountSyncStatus>(`/bankaccount/${accountId}/sync`)).value;
  const giveUpAt = Date.now() + SYNC_WAIT_MS;
  let failedPolls = 0;
  while (status && (status.state === 'queued' || status.state === 'running')) {
    onProgress?.(status);
    if (Date.now() > giveUpAt) {
      throw new Error('The sync is still running; its movements will appear when it finishes.');
    }
    await new Promise((resolve) => setTimeout(resolve, SYNC_POLL_MS));
    try {
      status = (await get<BankAccountSyncStatus>(`/bankaccount/${accountId}/sync`)).value;
      failedPolls = 0;
    } catch (error) {
      // The sync runs on the server regardless: a blip in the status poll must not end the wait.
      if (++failedPolls >= 3) {
        throw error;
      }
    }
  }

  if (!status || status.state !== 'completed') {
    throw new Error(status?.message || 'The sync did not complete.');
  }
  return status;
};

// Billing API methods

const getBillingStatus = async (): Promise<Response<BillingStatus>> => {
  return get<BillingStatus>('/billing/status');
};

const getPlans = async (): Promise<Response<PlanDto[]>> => {
  return get<PlanDto[]>('/billing/plans');
};

const getCreditPacks = async (): Promise<Response<CreditPackDto[]>> => {
  return get<CreditPackDto[]>('/billing/credits/packs');
};

const getCreditsBalance = async (): Promise<Response<{ balance: number; usedThisMonth: number }>> => {
  return get<{ balance: number; usedThisMonth: number }>('/billing/credits');
};

const addToCart = async (planId: number, productId: number): Promise<Response<unknown>> => {
  return post<unknown>('/billing/cart/add', { planId, productId });
};

const checkout = async (paymentType: string): Promise<Response<unknown>> => {
  return post<unknown>('/billing/checkout', { paymentType });
};

const initiateCheckout = async (): Promise<Response<CheckoutInitiateData>> => {
  return post<CheckoutInitiateData>('/billing/checkout/initiate');
};

const getCheckoutStatus = async (paymentId: number): Promise<Response<CheckoutStatusData>> => {
  return get<CheckoutStatusData>(`/billing/checkout/${paymentId}/status`);
};

const getPaymentHistory = async (): Promise<Response<PaymentDto[]>> => {
  return get<PaymentDto[]>('/billing/payments');
};

// Product updates (notification bell). The BFF answers empty / 0 when the Notification service
// is unavailable, so these never break the header.

const getProductUpdates = async (locale: string): Promise<Response<ProductUpdatesResponse>> => {
  return get<ProductUpdatesResponse>('/product-updates', { locale });
};

const getProductUpdatesUnreadCount = async (): Promise<Response<number>> => {
  return get<number>('/product-updates/unread-count');
};

const markProductUpdateRead = async (id: string): Promise<Response<boolean>> => {
  return post<boolean>(`/product-updates/${encodeURIComponent(id)}/read`);
};

const markAllProductUpdatesRead = async (): Promise<Response<boolean>> => {
  return post<boolean>('/product-updates/read-all');
};

// Notification settings: product updates by email (opt-in). Unlike the bell, a failed save
// rejects, so the settings page can put the switch back.
const getProductUpdatePreferences = async (): Promise<Response<ProductUpdatePreferences>> => {
  return get<ProductUpdatePreferences>('/product-updates/preferences');
};

const setProductUpdatePreferences = async (emailEnabled: boolean): Promise<Response<ProductUpdatePreferences>> => {
  return put<ProductUpdatePreferences>('/product-updates/preferences', { emailEnabled });
};

// Export as service object (matches MagniInbox pattern)
export const apiService = {
  get,
  post,
  put,
  delete: del,
  getSibsAccounts,
  getSibsBalances,
  getSibsTransactions,
  getMyConsents,
  getConsentStatus,
  finalizeConsent,
  syncAccount,
  getBillingStatus,
  getPlans,
  getCreditPacks,
  getCreditsBalance,
  addToCart,
  checkout,
  initiateCheckout,
  getCheckoutStatus,
  getPaymentHistory,
  getProductUpdates,
  getProductUpdatesUnreadCount,
  markProductUpdateRead,
  markAllProductUpdatesRead,
  getProductUpdatePreferences,
  setProductUpdatePreferences,
};

export { ApiError };
